import React from "react";

interface AddonStepperProps {
  value: number;
  max: number;
  onChange: (value: number) => void;
  label: string;
  decreaseLabel: string;
  increaseLabel: string;
  disabled?: boolean;
  testId?: string;
}

const stepBtn =
  "flex h-9 w-9 items-center justify-center rounded-full font-display text-lg font-extrabold " +
  "text-ink transition-colors duration-150 hover:bg-teal hover:text-white " +
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal " +
  "disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-ink";

/** Extra-subject count picker, 0..max. Replaces a native <select> so it matches the site theme. */
const AddonStepper = ({
  value,
  max,
  onChange,
  label,
  decreaseLabel,
  increaseLabel,
  disabled = false,
  testId,
}: AddonStepperProps) => {
  const set = (next: number) => {
    if (!disabled && next >= 0 && next <= max && next !== value) onChange(next);
  };

  return (
    <div
      role="group"
      aria-label={label}
      data-testid={testId}
      data-value={value}
      onKeyDown={(e) => {
        if (e.key === "ArrowUp" || e.key === "ArrowRight") {
          e.preventDefault();
          set(value + 1);
        } else if (e.key === "ArrowDown" || e.key === "ArrowLeft") {
          e.preventDefault();
          set(value - 1);
        }
      }}
      className="inline-flex items-center gap-1 rounded-full border-[1.5px] border-hairline bg-surface p-1"
    >
      <button
        type="button"
        aria-label={decreaseLabel}
        data-testid={testId && `${testId}-dec`}
        disabled={disabled || value <= 0}
        onClick={() => set(value - 1)}
        className={stepBtn}
      >
        −
      </button>
      <output aria-live="polite" className="w-8 text-center font-display text-lg font-extrabold">
        {value}
      </output>
      <button
        type="button"
        aria-label={increaseLabel}
        data-testid={testId && `${testId}-inc`}
        disabled={disabled || value >= max}
        onClick={() => set(value + 1)}
        className={stepBtn}
      >
        +
      </button>
    </div>
  );
};

export default AddonStepper;
